/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';

import { StatsigProvider } from '@coursera-web/statsig';
import * as Sentry from '@sentry/react';

import { buildHashWithAuthMode, getLocationHash } from 'js/lib/authModeDeepLinkUtils';
import { useLocation, useNavigate } from 'js/lib/useRouter';

import { LoadingSection } from '@coursera/cds-core';

import * as actions from 'bundles/authentication-common/components/actions';
import type { OnAuthenticationOptions } from 'bundles/authentication-common/components/actions';
import AuthenticationContentTracker from 'bundles/authentication-common/components/authenticationModal/AuthenticationContentTracker';
import CompleteSSO from 'bundles/authentication-common/components/authenticationModal/CompleteSSO';
import ExpiredLink from 'bundles/authentication-common/components/authenticationModal/ExpiredLink';
import ExpiredSSOLink from 'bundles/authentication-common/components/authenticationModal/ExpiredSSOLink';
import ForgotPassword from 'bundles/authentication-common/components/authenticationModal/ForgotPassword';
import LinkedAccount from 'bundles/authentication-common/components/authenticationModal/LinkedAccount';
import Login from 'bundles/authentication-common/components/authenticationModal/Login';
import MagicLink from 'bundles/authentication-common/components/authenticationModal/MagicLink';
import OpenAIAuth from 'bundles/authentication-common/components/authenticationModal/OpenAI/OpenAIAuth';
import OrganizationSSOLogin from 'bundles/authentication-common/components/authenticationModal/OrganizationSSO/OrganizationSSOLogin';
import OrganizationSSOSignup from 'bundles/authentication-common/components/authenticationModal/OrganizationSSO/OrganizationSSOSignup';
import RequestMagicLink from 'bundles/authentication-common/components/authenticationModal/RequestMagicLink';
import SSO from 'bundles/authentication-common/components/authenticationModal/SSO';
import Signup from 'bundles/authentication-common/components/authenticationModal/Signup';
import UnifiedAuthEntry from 'bundles/authentication-common/components/authenticationModal/UnifiedAuthEntry';
import type SSOError from 'bundles/authentication-common/components/sso/SSOError';
import { AUTHENTICATION_FORM_VARIANTS, AUTHENTICATION_MODAL_MODES } from 'bundles/authentication-common/constants';
import type { AUTHENTICATION_MODAL_MODES_KEY, LoginMethodType } from 'bundles/authentication-common/constants';
import { AuthModalTrackerContextProvider } from 'bundles/authentication-common/contexts/AuthenticationTracking';
import useEnterpriseSsoEmailCheck from 'bundles/authentication-common/hooks/useEnterpriseSsoCheck';
import useForceSSOLoginQuery from 'bundles/authentication-common/hooks/useForceSSOLoginQuery';
import useHandleInvalidDomainError from 'bundles/authentication-common/hooks/useHandleInvalidDomainError';
import useUserThirdPartyOrganizationsQuery from 'bundles/authentication-common/hooks/useUserThirdPartyOrganizationsQuery';
import useWhitelistOrgForSSO from 'bundles/authentication-common/hooks/useWhitelistOrgForSSO';
import { isTermsOfUseRolloutForUS } from 'bundles/authentication-common/utils';
import { getModalTrackerValue } from 'bundles/authentication-common/utils/eventingUtils';
import { loginMethodsToAuthModeWithMetadata } from 'bundles/authentication-common/utils/navigationUtils';
import { useSsoLockedOutStatus } from 'bundles/unified-common/hooks/useSsoLockedOutStatus';
import type { AuthenticationContentBaseProps } from 'bundles/user-account/types';

import _t from 'i18n!nls/authentication';

type Context = Record<string, any>;

export type Props = AuthenticationContentBaseProps & {
  context: Context;
  isStandaloneAuth?: boolean;
  hideOrganizationSSO?: boolean;
  preferredOrgIds?: string[];
};

const getAuthViews: (showProgressiveAuth: boolean) => Record<AUTHENTICATION_MODAL_MODES_KEY, any> = (
  showProgressiveAuth
) => ({
  complete: CompleteSSO,
  error: Login,
  expired: ExpiredLink,
  expiredSSO: ExpiredSSOLink,
  forgot: ForgotPassword,
  linked: LinkedAccount,
  login: showProgressiveAuth ? UnifiedAuthEntry : Login,
  logincontinue: Login,
  magiclink: MagicLink,
  organizationlogin: OrganizationSSOLogin,
  organizationsignup: OrganizationSSOSignup,
  requestmagiclink: RequestMagicLink,
  signup: showProgressiveAuth ? UnifiedAuthEntry : Signup,
  signupcontinue: Signup,
  sso: SSO,
  openaiauth: OpenAIAuth,
});

const styles = {
  container: css`
    max-height: 100%;
    height: 100vh;
    overflow: auto;
    position: relative;

    @media (min-width: 600px) {
      height: auto;
    }
  `,
  loader: css`
    width: 100%;
    justify-content: center;
    align-self: center;
    overflow: hidden;
  `,
};

const AuthenticationContent = ({
  context: contextFromProps,
  defaultMode = AUTHENTICATION_MODAL_MODES.LOGIN,
  isStandaloneAuth,
  redirectTo,
  ssoRedirectTo,
  magicLinkRedirect,
  preventRegister,
  switchFormsByQueryParam = true,
  hideOrganizationSSO = false,
  programId,
  preferredOrgIds,
}: Props) => {
  const [context, setContext] = useState<Context>(contextFromProps ?? {});
  const [ssoError, setSsoError] = useState<SSOError | undefined>();
  useEffect(() => {
    setContext((prevContext = {}) => {
      const filteredContext = Object.fromEntries(
        Object.entries(contextFromProps || {}).filter(([, value]) => value !== undefined)
      );
      return { ...prevContext, ...filteredContext };
    });
  }, [contextFromProps]);

  const [mode, setMode] = useState<AUTHENTICATION_MODAL_MODES_KEY>(defaultMode);
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();

  const { authProvider, showProgressiveAuth } = context;

  const { organizations, loading: isThirdPartyLoading } = useUserThirdPartyOrganizationsQuery(authProvider);

  const organizationId = organizations?.[0]?.id;
  const { forceSSOLogin: isForceSSOLogin, loading: isForceSSOLoading } = useForceSSOLoginQuery(organizationId || '');
  const { isOrgOrProgramWhiteListedForSSOLogin, isLoading: isWhitelistOrgForSSOLoginLoading } = useWhitelistOrgForSSO({
    thirdPartyOrganizationId: organizationId,
    programId,
  });
  const { isSsoLockedOut, isLoading: isSsoLockedOutLoading } = useSsoLockedOutStatus(organizationId);
  const forceSSOLogin = isSsoLockedOut || isForceSSOLogin;

  // add epic variable for XDPs CRO
  const onNavigate = (
    mode: AUTHENTICATION_MODAL_MODES_KEY,
    options: Context = {},
    shouldSwitchFormsByQueryParam = switchFormsByQueryParam
  ) => {
    const areModesTheSame = location.pathname.includes(`/${mode}`);
    const searchParams = new URLSearchParams(location.search);
    if (options.clearAuthError) {
      searchParams.delete('token');
      searchParams.delete('authError');
      setSsoError(undefined);
    }
    if (
      isStandaloneAuth &&
      !areModesTheSame &&
      (mode === AUTHENTICATION_MODAL_MODES.SIGNUP || mode === AUTHENTICATION_MODAL_MODES.LOGIN)
    ) {
      navigate(`/${mode}?${searchParams.toString()}`, { replace: true });
    } else {
      if (shouldSwitchFormsByQueryParam) {
        searchParams.delete('authMode');
        const searchString = searchParams.toString();
        navigate(
          {
            pathname: location.pathname,
            search: searchString ? `?${searchString}` : '',
            hash: buildHashWithAuthMode(getLocationHash(location), mode),
          },
          { replace: true }
        );
      }
      setContext((prevContext) => ({ ...prevContext, ...options }));
      setMode(mode);
    }
  };

  const onLogin = (redirectURL?: string) => {
    actions.onLogin(redirectTo || redirectURL);
  };

  const onSignup = (redirectURL?: string, options?: OnAuthenticationOptions) => {
    actions.onSignup(redirectTo || redirectURL, options);
  };

  const onSSOError = (error: SSOError) => {
    const { code } = error;
    switch (code) {
      // For enforceSsoV2 - SSO v2 and loginDisabledForDomain, where we enforce enterprise sso login, we just set the error state and don't navigate
      case 'enforceSsoV2':
      case 'loginDisabledForDomain':
        return setSsoError(error);
      case 'accountLinkedToSocialAccount':
        return onNavigate(AUTHENTICATION_MODAL_MODES.LINKED, { accountType: error.response?.existingAccountType });
      case 'existingCourseraAccount':
      case 'noEmailAvailable':
      default:
        return onNavigate(AUTHENTICATION_MODAL_MODES.COMPLETE, {
          mode: code,
          referrer: mode,
          service: error.service,
        });
    }
  };

  const resetSsoError = () => {
    setSsoError(undefined);
  };

  const enterpriseSsoCheckOptions = useMemo(
    () => ({ preferredOrgIds, returnTo: ssoRedirectTo }),
    [preferredOrgIds, ssoRedirectTo]
  );
  const enterpriseSsoEmailCheckInfo = useEnterpriseSsoEmailCheck(enterpriseSsoCheckOptions);
  const { checkForceSSO } = enterpriseSsoEmailCheckInfo;

  const navigateToPasswordStep = (email: string) => {
    onNavigate(
      AUTHENTICATION_MODAL_MODES.LOGINCONTINUE,
      { defaultEmail: email, formVariant: AUTHENTICATION_FORM_VARIANTS.PASSWORD },
      false
    );
  };

  const routeByLoginMethods = (loginMethods: Array<LoginMethodType>, email: string) => {
    const {
      mode: nextMode,
      navigationOptions: options,
      shouldSwitchFormsByQueryParam,
    } = loginMethodsToAuthModeWithMetadata({ loginMethods, email });

    onNavigate(nextMode, options, shouldSwitchFormsByQueryParam);
  };

  // Owns the full email-entry sequence (force-SSO check, conditional login-methods
  // lookup, navigation) so the routing decision reads the local `checkForceSSO` await
  // result rather than the hook's `isEnterpriseSsoLogin` React state, which can lag a
  // render behind the API response. Errors propagate to the form's onError handler.
  const handleEmailSubmit = async (email: string): Promise<void> => {
    const forceSsoResult = await checkForceSSO(email);
    if (forceSsoResult?.isForceSSOLogin) {
      navigateToPasswordStep(email);
      return;
    }

    const response = await actions.getLoginMethods(email);
    routeByLoginMethods(response?.loginMethods ?? [], email);
  };

  const handleOnFormError = (email: string) => {
    onNavigate(
      mode === AUTHENTICATION_MODAL_MODES.SIGNUP
        ? AUTHENTICATION_MODAL_MODES.SIGNUPCONTINUE
        : AUTHENTICATION_MODAL_MODES.LOGINCONTINUE,
      { defaultEmail: email, formVariant: AUTHENTICATION_FORM_VARIANTS.PASSWORD },
      false
    );
  };

  const checkIsSSOLockedOut = async (email: string): Promise<boolean> => {
    if (!email) {
      return false;
    }

    const forceSsoResult = await checkForceSSO(email);
    return !!forceSsoResult?.isForceSSOLogin;
  };

  const navigateBasedOnLoginMethods = async (email: string): Promise<void> => {
    if (!email) {
      return;
    }

    try {
      const response = await actions.getLoginMethods(email);

      // Defensive check: default to empty array if loginMethods is undefined
      const loginMethods = response?.loginMethods ?? [];

      // Force-SSO is already excluded by checkIsSSOLockedOut before this runs.
      routeByLoginMethods(loginMethods, email);
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          message: error?.responseJSON?.msg,
        },
      });

      handleOnFormError(email);
    }
  };

  useHandleInvalidDomainError({
    setSsoError,
    setIsAuthLoading,
    checkIsSSOLockedOut,
    navigateBasedOnLoginMethods,
  });

  const onSSOSuccess = (isRegistration: boolean, hasSavedOnboardingOnSignup?: boolean) => {
    if (isRegistration) {
      onSignup(undefined, { addShowTouAcceptParam: isTermsOfUseRolloutForUS(), hasSavedOnboardingOnSignup });
    } else {
      onLogin();
    }
  };

  const ssoContext = {
    forceSSOLogin,
    organizations,
    shouldShowCustomSSO: isOrgOrProgramWhiteListedForSSOLogin,
    areAnyApiRequestsInFlight:
      isThirdPartyLoading || isForceSSOLoading || isWhitelistOrgForSSOLoginLoading || isSsoLockedOutLoading,
    ssoError,
    resetSsoError,
    enterpriseSsoEmailCheckInfo,
    ssoRedirectTo,
    onSubmit: handleEmailSubmit,
    onError: handleOnFormError,
  };

  const Content = (getAuthViews(showProgressiveAuth)[mode] || Login) as unknown as React.ComponentType<any>;
  return (
    <StatsigProvider>
      <section css={styles.container} className="authentication-box-content">
        <AuthModalTrackerContextProvider
          value={getModalTrackerValue({
            mode,
            isProgressiveAuth: showProgressiveAuth,
            formVariant: context.formVariant,
            socialSsoService: context.thirdPartyAuthService,
          })}
        >
          <AuthenticationContentTracker />
          {isAuthLoading ? (
            <LoadingSection label={_t('Loading...')} css={styles.loader} className="unified-auth-entry-loading" />
          ) : (
            <Content
              redirect={redirectTo}
              magicLinkRedirect={magicLinkRedirect}
              navigate={onNavigate}
              onLogin={onLogin}
              onSignup={onSignup}
              onSSOError={onSSOError}
              onSSOSuccess={onSSOSuccess}
              preventRegister={preventRegister}
              hideOrganizationSSO={hideOrganizationSSO}
              {...ssoContext}
              {...context}
            />
          )}
        </AuthModalTrackerContextProvider>
      </section>
    </StatsigProvider>
  );
};

export default AuthenticationContent;
